import { t } from "@/lib/i18n";
import type { DayProgress } from "../../../yap-frontend-rs/pkg";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

const DAY_LABELS = [
  t("weekday-monday-initial"),
  t("weekday-tuesday-initial"),
  t("weekday-wednesday-initial"),
  t("weekday-thursday-initial"),
  t("weekday-friday-initial"),
  t("weekday-saturday-initial"),
  t("weekday-sunday-initial"),
];

export function WeekProgressStrip({
  week,
  className = "",
}: {
  week: DayProgress[];
  className?: string;
}) {
  return (
    <div className={`flex w-full items-stretch ${className}`}>
      {week.map((day, i) => (
        <DayCell key={i} day={day} index={i} />
      ))}
    </div>
  );
}

function DayCell({ day, index }: { day: DayProgress; index: number }) {
  const fillPercent =
    day.is_future || day.target_seconds === 0
      ? 0
      : Math.min(100, (day.seconds / day.target_seconds) * 100);

  const borderL = "";
  const rounded =
    index === 0 ? "rounded-l-lg" : index === 6 ? "rounded-r-lg" : "";
  const todayClasses = day.is_today
    ? "scale-110 z-10 rounded-lg shadow-lg border border-border"
    : "";

  const content = (
    <>
      <span className="text-lg font-semibold leading-none tabular-nums">
        {day.is_future
          ? "·"
          : `+${day.new_cards + day.learned_cards + day.locked_in_cards}`}
      </span>
      <span className="text-[10px] uppercase tracking-wide opacity-70">
        {DAY_LABELS[index]}
      </span>
    </>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`flex-1 relative overflow-hidden ${borderL} ${rounded} ${todayClasses} ${day.is_future ? "bg-muted/30" : `backdrop-brightness-105 backdrop-saturate-120 dark:backdrop-brightness-100 backdrop-blur-sm ${day.seconds > 0 ? "bg-foreground/10" : ""}`}`}
        >
          {/* Green fill bar */}
          {!day.is_future && fillPercent > 0 && (
            <div
              className={`absolute inset-0 ${fillPercent >= 100 ? "bg-foreground" : "bg-foreground/90"}`}
              style={{ clipPath: `inset(${100 - fillPercent}% 0 0 0)` }}
            />
          )}

          {/* Base text layer (normal foreground color) */}
          <div
            className={`relative flex flex-col items-center justify-center py-3 gap-0.5 ${day.is_future ? "text-muted-foreground/60" : "text-foreground"}`}
          >
            {content}
          </div>

          {/* Clipped text layer (white, revealed by fill) */}
          {!day.is_future && fillPercent > 0 && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center py-3 gap-0.5 text-background"
              style={{ clipPath: `inset(${100 - fillPercent}% 0 0 0)` }}
            >
              {content}
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {day.is_future
          ? t("week-progress-strip-upcoming")
          : day.reviews === 0
            ? t("week-progress-strip-no-activity")
            : t("review-week-activity", {
                added: day.new_cards,
                learned: day.learned_cards + day.locked_in_cards,
              })}
      </TooltipContent>
    </Tooltip>
  );
}
